// OurProducts.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Usrnav from '../../usercomponents/Usrnav/Usrnav';
import styles from './OurProducts.module.css';
import config from '../../config/config';
import Usrsearch from '../../usercomponents/Usrsearch/Usrsearch';

function getSimilarity(a, b) {
  if (!a || !b) return 0;
  a = a.toLowerCase();
  b = b.toLowerCase();
  let longer = a.length > b.length ? a : b;
  let shorter = a.length > b.length ? b : a;
  let longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
}

function editDistance(s1, s2) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();
  const costs = Array(s2.length + 1).fill(0);
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1))
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}

const OurProducts = () => {
  const [ourProducts, setOurProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartProductIds, setCartProductIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const navigate = useNavigate();

   useEffect(() => {
    async function fetchProducts() {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/products`);
        setOurProducts(response.data);
      } catch (err) {
        console.error('Failed to fetch products', err);
      } finally {
        setLoading(false);
      }
    }

    async function fetchUserCart() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const res = await axios.get(`${config.apiBaseUrl}/api/users/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const ids = res.data.map(item => item.productId);
        setCartProductIds(ids);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }

    fetchProducts();
    fetchUserCart();
  }, []);

  useEffect(() => {
    const fetchOurProducts = async () => {
      try {
        const res = await axios.get(`${config.apiBaseUrl}/api/products/ourproducts`);
        setOurProducts(res.data);
      } catch (err) {
        console.error('Error fetching Our Products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOurProducts();
  }, []);

  if (loading) return <div className={styles.loading}>Loading...</div>;

  const handleAddToCart = async (product) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Login to Add items in Cart');
      navigate('/shreeohm/login');
      return;
    }
    if (cartProductIds.includes(product._id)) {
      alert('Product is Already added');
      return;
    }
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/users/addtocart`,
        { productId: product._id, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message);
      console.log('Cart after adding:', response.data.cart);
      setCartProductIds(prev => [...prev, product._id]);
      setTimeout(() => navigate('/mycart'), 1000);
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      console.error('Error adding product to cart:', status, message);
      if (status === 403) {
        alert('Session expired, login to Add Products');
        localStorage.removeItem('token');
        navigate('/shreeohm/login');
      } else {
        alert('Error adding product to cart');
      }
    }
  };

  const productSuggestions = ourProducts
    .map(p => p.productName)
    .filter(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 5);

  const filteredProducts = ourProducts
    .map(product => {
      const similarity = getSimilarity(searchTerm, product.productName);
      return { ...product, similarity };
    })
    .filter(product => {
      const matchesTags = Array.isArray(product.tags) &&
        product.tags.some(tag =>
          getSimilarity(tag, searchTerm) > 0.6 ||
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesCategories = Array.isArray(product.categories) &&
        product.categories.some(category =>
          getSimilarity(category, searchTerm) > 0.6 ||
          category.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesSearch =
        product.similarity > 0.6 ||
        product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        matchesTags ||
        matchesCategories;

      const matchesMinPrice =
        minPrice === '' || product.pdprice.npr >= parseInt(minPrice);

      const matchesMaxPrice =
        maxPrice === '' || product.pdprice.npr <= parseInt(maxPrice);

      return matchesSearch && matchesMinPrice && matchesMaxPrice;
    })
    .sort((a, b) => b.similarity - a.similarity);

  return (
    <div className={styles.opcontainer}>
      <div className="nav-wrapper">
        <Usrnav />
      </div>

      <div className="search-wrapper">
        <Usrsearch
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          suggestions={productSuggestions}
        />
      </div>

      <section className={styles.productsSection}>
        <h2 className={styles.sectionTitle}>Our Products</h2>
        <div className={styles.productsGrid}>
          {(searchTerm ? filteredProducts : ourProducts).map(product => (
            <div key={product._id} className={styles.productCard}>
              <div className={styles.imageContainer}>
                <img
                  src={product.images?.[0]?.url || 'https://via.placeholder.com/300'}
                  alt={product.productName}
                  className={styles.productImage}
                  loading="lazy"
                />
                <div className={styles.overlay}></div>
              </div>
              <div className={styles.productInfo}>
                <h3 className={styles.productName}>{product.productName}</h3>
                <p className={styles.productDescription}>
                  {product.productDetails.slice(0, 100)}...
                </p>
                <p className={styles.productPrice}>Rs. {product.pdprice?.npr}</p>
                <div className="product-buttons">
                  <button
                    className="buy-now"
                    onClick={() => navigate(`/buynow/${product._id}`, { state: { product } })}
                  >
                    Buy Now
                  </button>
                  <button
                    className="add-to-cart"
                    onClick={() => handleAddToCart(product)}
                    style={{
                      backgroundColor: cartProductIds.includes(product._id) ? 'limegreen' : '',
                      color: cartProductIds.includes(product._id) ? 'white' : ''
                    }}
                  >
                    {cartProductIds.includes(product._id) ? 'Added to Cart ✅' : 'Add to Cart 🛒'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default OurProducts;
